import { ReactNode, useEffect, useMemo } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  LearningIssueStudentFeedback,
  Role,
  StudentFeedback,
  StudentFeedbackIssue,
  StudentFeedbackStatus,
  studentFeedbackStatusSchema,
  StudentFeedbackStatusLabelMap,
} from '@airreading/common/data';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { MultiSelectOption } from '@/components/ui/multi-select';
import {
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
} from '@/components/ui/multi-select-utils';
import { useToast } from '@/components/ui/hooks/use-toast';

import { bulkUpdateStudentFeedbacks, getStudentFeedbacks } from '@/lib/api-calls';
import { useAllLearningPaths, userTypeaheadSearch } from '@/lib/api-call-hooks';
import { airServerErrorFrom } from '@/lib/error-handling';

// Schema for editing student feedback
const formEditStudentFeedbackSchema = z
  .object({
    reviewer: z.object({ value: z.string(), label: z.string() }),
    status: studentFeedbackStatusSchema,
    note: z.string().optional(),
    learningPath: z.object({ value: z.string(), label: z.string() }).optional(),
    startingLesson: z.object({ value: z.string(), label: z.string() }).optional(),
  })
  .superRefine((data, ctx) => {
    // If learning path is provided, starting lesson must also be provided, and vice versa
    // Follow the same pattern as formCreateStudentFeedbackSchema - check value directly
    // emptySelectOption has value: '', which is falsy, so !value will be true for empty
    const hasLearningPath = !!data.learningPath?.value;
    const hasStartingLesson = !!data.startingLesson?.value;

    if (hasLearningPath && !hasStartingLesson) {
      ctx.addIssue({
        path: ['startingLesson'],
        code: z.ZodIssueCode.custom,
        message: 'New starting lesson is required when learning path is provided',
      });
    }

    if (hasStartingLesson && !hasLearningPath) {
      ctx.addIssue({
        path: ['learningPath'],
        code: z.ZodIssueCode.custom,
        message: 'Learning path is required when new starting lesson is provided',
      });
    }
  });

type Form = z.infer<typeof formEditStudentFeedbackSchema>;
type NameLike = { id: string; name: string };

export interface EditStudentFeedbackModalProps {
  studentFeedback: LearningIssueStudentFeedback | StudentFeedback;
  trigger?: ReactNode;
  onSuccess?: () => void;
}

function hasLearningIssueStudentFallbacks(
  feedback: LearningIssueStudentFeedback | StudentFeedback,
): feedback is LearningIssueStudentFeedback {
  return 'studentLearningPath' in feedback;
}

function feedbackDefaultsContext(feedback: LearningIssueStudentFeedback | StudentFeedback): {
  currentLearningPath: NameLike | null;
  currentStartingLesson: NameLike | null;
  studentLearningPathId: string | null;
  studentStartingLessonId: string | null;
} {
  if (hasLearningIssueStudentFallbacks(feedback)) {
    return {
      currentLearningPath: feedback.studentLearningPath,
      currentStartingLesson: feedback.studentStartingLesson,
      studentLearningPathId: null,
      studentStartingLessonId: null,
    };
  }

  return {
    currentLearningPath: feedback.learningPath,
    currentStartingLesson: feedback.startingLesson,
    studentLearningPathId: feedback.student.learningPathId,
    studentStartingLessonId: feedback.student.startingLessonId,
  };
}

export const EditStudentFeedbackModal = ({ studentFeedback, trigger, onSuccess }: EditStudentFeedbackModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const feedback = studentFeedback;
  const { currentLearningPath, currentStartingLesson, studentLearningPathId, studentStartingLessonId } =
    feedbackDefaultsContext(feedback);

  const { mutateAsync: updateStudentFeedbackMutate } = useMutation({
    mutationFn: bulkUpdateStudentFeedbacks,
  });

  const isLearningIssue = feedback.studentFeedbackIssue === StudentFeedbackIssue.learning_issue;
  const isResolved = feedback.status === StudentFeedbackStatus.resolved;

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<Form>) => {
    const { learningPath, startingLesson, reviewer, ...rest } = formData;

    const updateOptions: Parameters<typeof bulkUpdateStudentFeedbacks>[0]['options'] = {
      reviewerId: reviewer.value,
      status: rest.status,
      note: rest.note,
    };

    // Only include learning path and starting lesson if both are provided
    if (startingLesson?.value && learningPath?.value) {
      updateOptions.learningPathId = learningPath.value;
      updateOptions.startingLessonId = startingLesson.value;
    } else {
      // If not both provided, set to undefined (not null) to avoid clearing existing values
      updateOptions.learningPathId = undefined;
      updateOptions.startingLessonId = undefined;
    }

    return updateStudentFeedbackMutate({
      studentFeedbackIds: [feedback.id],
      options: updateOptions,
    })
      .then(() => {
        toast({ title: 'Issue updated successfully' });
        onSuccess?.();
        closeModal();
        reset();
        queryClient.invalidateQueries({ queryKey: getStudentFeedbacks().queryKey });
      })
      .catch((error) => {
        toast({
          title: airServerErrorFrom(error)?.userFacingMessage || 'Failed to update issue. Please try again.',
          variant: 'destructive',
        });
      });
  };

  const defaultValues: DefaultValues<Form> = useMemo(() => {
    const currentLearningPathOption = currentLearningPath
      ? buildNameLikeSelectOptions(currentLearningPath)
      : emptySelectOption;
    const currentStartingLessonOption = currentStartingLesson
      ? buildNameLikeSelectOptions(currentStartingLesson)
      : emptySelectOption;

    return {
      reviewer: feedback.reviewer ? buildUserSelectOptions(feedback.reviewer) : emptySelectOption,
      status: feedback.status,
      note: feedback.note ?? undefined,
      learningPath: isLearningIssue
        ? feedback.learningPath
          ? buildNameLikeSelectOptions(feedback.learningPath)
          : currentLearningPathOption
        : emptySelectOption,
      startingLesson: isLearningIssue
        ? feedback.startingLesson
          ? buildNameLikeSelectOptions(feedback.startingLesson)
          : currentStartingLessonOption
        : emptySelectOption,
    };
  }, [
    currentLearningPath,
    currentStartingLesson,
    feedback.reviewer,
    feedback.status,
    feedback.note,
    feedback.learningPath,
    feedback.startingLesson,
    isLearningIssue,
  ]);

  return (
    <FormModal
      modalTitle="Edit issue"
      dialogContentProps={{ className: 'max-w-2xl' }}
      formSchema={formEditStudentFeedbackSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel="Submit"
      shouldResetOnCancel
      resetOnDefaultValuesChange
    >
      {({ form }) => (
        <EditStudentFeedbackForm
          form={form}
          isLearningIssue={isLearningIssue}
          isResolved={isResolved}
          studentLearningPathId={studentLearningPathId}
          studentStartingLessonId={studentStartingLessonId}
        />
      )}
    </FormModal>
  );
};

interface EditStudentFeedbackFormProps {
  form: UseFormReturn<Form>;
  isLearningIssue: boolean;
  isResolved: boolean;
  studentLearningPathId: string | null;
  studentStartingLessonId: string | null;
}

const EditStudentFeedbackForm = ({
  form,
  isLearningIssue,
  isResolved,
  studentLearningPathId,
  studentStartingLessonId,
}: EditStudentFeedbackFormProps) => {
  const { watch, setValue, clearErrors, getValues } = form;
  const queryClient = useQueryClient();

  const { learningPaths: availableLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const learningPathOptions: MultiSelectOption[] = useMemo(
    () => availableLearningPaths.map(buildNameLikeSelectOptions).sort((a, b) => a.label.localeCompare(b.label)),
    [availableLearningPaths],
  );

  const selectedComboLearningPathId = watch('learningPath')?.value;
  const selectedLearningPath = useMemo(
    () => availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId),
    [availableLearningPaths, selectedComboLearningPathId],
  );
  const selectableLessons: MultiSelectOption[] = useMemo(
    () => (selectedLearningPath?.learningPathLessons || []).map(({ lesson }) => buildNameLikeSelectOptions(lesson)),
    [selectedLearningPath],
  );

  const allLessonsById = useMemo(
    () =>
      new Map(
        availableLearningPaths.flatMap((lp) =>
          lp.learningPathLessons.map(({ lesson }) => [lesson.id, lesson] as const),
        ),
      ),
    [availableLearningPaths],
  );

  useEffect(() => {
    if (!isLearningIssue || !studentLearningPathId || !studentStartingLessonId || availableLearningPaths.length === 0)
      return;

    const currentLearningPathValue = getValues('learningPath')?.value;
    const currentStartingLessonValue = getValues('startingLesson')?.value;
    if (currentLearningPathValue || currentStartingLessonValue) return;

    const learningPath = availableLearningPaths.find((lp) => lp.id === studentLearningPathId);
    if (!learningPath) return;

    const startingLesson =
      learningPath.learningPathLessons.find(({ lesson }) => lesson.id === studentStartingLessonId)?.lesson ??
      allLessonsById.get(studentStartingLessonId);
    if (!startingLesson) return;

    setValue('learningPath', buildNameLikeSelectOptions(learningPath), { shouldDirty: false, shouldValidate: true });
    setValue('startingLesson', buildNameLikeSelectOptions(startingLesson), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [
    isLearningIssue,
    studentLearningPathId,
    studentStartingLessonId,
    availableLearningPaths,
    allLessonsById,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    const sub = watch((value, { name, type }) => {
      // Clear starting lesson only on user-driven learning path changes.
      if (name === 'learningPath' && type === 'change' && value.startingLesson?.value) {
        setValue('startingLesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }

      // Clear errors when form values are valid (both filled or both empty)
      // This prevents errors from showing when modal opens/resets or closes
      const hasLearningPath = !!value.learningPath?.value;
      const hasStartingLesson = !!value.startingLesson?.value;
      if ((hasLearningPath && hasStartingLesson) || (!hasLearningPath && !hasStartingLesson)) {
        clearErrors(['learningPath', 'startingLesson']);
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, clearErrors, form]);

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-2">
        <FormComboBox
          form={form}
          label="Reviewer"
          name="reviewer"
          placeholder="Select admin"
          {...comboBoxOptionsOf(userTypeaheadSearch(queryClient, { roles: [Role.air_admin, Role.air_limited_admin] }))}
          triggerProps={{ className: 'w-full' }}
          triggerSearchOnFocus
        />
        <FormSingleSelect
          name="status"
          label="Issue status"
          form={form}
          options={studentFeedbackStatusSchema.options}
          renderOption={(value: StudentFeedbackStatus) => StudentFeedbackStatusLabelMap[value]}
          triggerProps={{ className: 'w-full', disabled: isResolved }}
        />
      </div>
      {isLearningIssue && (
        <div className="grid grid-cols-2 gap-2">
          <FormComboBox
            form={form}
            label="Learning path"
            name="learningPath"
            placeholder="Select learning path"
            {...comboBoxOptionsOf(learningPathOptions)}
            triggerProps={{ className: 'w-full', disabled: isLoadingLearningPaths }}
          />
          <FormComboBox
            form={form}
            label="New starting lesson"
            name="startingLesson"
            placeholder="Select starting lesson"
            {...comboBoxOptionsOf(selectableLessons)}
            triggerProps={{ className: 'w-full', disabled: isLoadingLearningPaths }}
          />
        </div>
      )}
      <FormTextArea name="note" label="Issue note" form={form} />
    </div>
  );
};
